import React, { useRef, useEffect } from 'react';
import QRCode from 'qrcode';

const Receipt = ({ order, onClose }) => {
  const receiptRef = useRef(null);
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    // Generate QR Code
    if (qrCanvasRef.current) {
      const qrValue = `${order.steadfastConsignmentId}-${order.orderId}`;
      QRCode.toCanvas(qrCanvasRef.current, qrValue, {
        width: 110,
        margin: 0,
        errorCorrectionLevel: 'M'
      });
    }
  }, [order]);

  const handlePrint = () => {
    window.print();
  };

  const printInNewWindow = () => {
    // First generate QR code as data URL from current canvas
    let qrDataUrl = '';
    if (qrCanvasRef.current) {
      qrDataUrl = qrCanvasRef.current.toDataURL('image/png');
    }

    const printWindow = window.open('', '_blank', 'width=400,height=600');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${order.steadfastConsignmentId || order.orderId}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-weight: bold;
          }
          
          body {
            font-family: 'Courier New', monospace;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f0f0f0;
            padding: 10px;
          }
          
          #receipt-container {
            width: 87.6mm;
            height: 101.6mm;
            background: white;
            position: relative;
            padding: 4mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.2);
            border: 1px solid #ddd;
          }

          .receipt-header {
            text-align: center;
            font-size: 18pt;
            font-weight: bold;
            margin-bottom: 3mm;
            padding: 0 2mm;
          }

          .receipt-divider {
            border: none;
            border-top: 2px solid #000;
            margin: 2mm 2mm;
          }

          .receipt-id {
            font-size: 12pt;
            font-weight: bold;
            margin-bottom: 2mm;
            margin-top: 2mm;
            text-align: center;
            padding: 0 2mm;
          }

          .receipt-phone {
            font-size: 10pt;
            font-weight: bold;
            margin-bottom: 2mm;
            padding: 0 2mm;
          }

          .customer-section {
            font-size: 9pt;
            font-weight: bold;
            line-height: 12pt;
            margin-bottom: 2mm;
            margin-top: 2mm;
            padding: 0 2mm;
          }

          .customer-phone {
            font-size: 11pt;
            font-weight: bold;
            margin-top: 2mm;
            margin-bottom: 2mm;
          }

          .customer-address {
            font-size: 9pt;
            font-weight: bold;
            line-height: 11pt;
          }

          .info-line {
            font-size: 10pt;
            font-weight: bold;
            line-height: 12pt;
            margin-bottom: 2mm;
            margin-top: 2mm;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 2mm;
          }

          .website-inline {
            font-size: 8pt;
            font-weight: bold;
          }

          .qr-container {
            text-align: center;
            margin: 8mm auto 3mm auto;
            padding: 0 2mm;
          }

          .qr-container img {
            margin: 0 auto;
            display: block;
            width: 110px;
            height: 110px;
          }

          .qr-label {
            font-size: 2px;
            font-weight: bold;
            margin-top: 1mm;
            margin-left: 2mm;
            text-align: left;
            padding: 0 2mm;
          }

          .footer {
            font-size: 8pt;
            font-weight: bold;
            position: absolute;
            bottom: 4mm;
            left: 4mm;
            right: 4mm;
            display: flex;
            justify-content: space-between;
            padding: 0 2mm;
          }

          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #3b82f6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 1000;
            font-family: Arial, sans-serif;
          }
          
          .print-button:hover {
            background: #2563eb;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            #receipt-container {
              box-shadow: none;
              border: none;
            }
            
            .print-button {
              display: none;
            }
          }
          
          @page {
            size: 87.6mm 101.6mm;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Receipt</button>
        
        <div id="receipt-container">
          <!-- Header - HowTech  -->
          <div class="receipt-header">
            HowTech 
          </div>

          <hr class="receipt-divider" />

          <!-- Receipt ID -->
          <div class="receipt-id">
            ${order.steadfastConsignmentId || order.orderId}
          </div>

          <!-- Phone -->
          <div class="receipt-phone">
            Call: +8801711893988
          </div>

          <!-- Customer Info -->
          <div class="customer-section">
            <div>Customer: ${order.customerInfo?.name || 'N/A'}</div>
            <div class="customer-phone">
              Phone: ${order.customerInfo?.phone || 'N/A'}
            </div>
            <div class="customer-address">
              Address: ${order.customerInfo?.address || 'N/A'}
            </div>
          </div>

          <!-- COD and Website -->
          <div class="info-line">
            <div>COD: ${order.codForCourier || order.totalAmount || '0.00'}</div>
            <div class="website-inline">www.howtech.com.bd</div>
          </div>

          <!-- QR Code -->
          <div class="qr-container">
            <img src="${qrDataUrl}" alt="QR Code" />
          </div>

          <!-- Order ID under QR -->
          <div class="qr-label">
            Order: ${order.steadfastConsignmentId}-${order.orderId}
          </div>

          <!-- Footer -->
          <div class="footer">
            <span>[HowTech ]</span>
            <span>${formatDateStatic()}</span>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const formatDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
  };

  // Helper function for the print window
  const formatDateStatic = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
  };

  return (
    <>
      <style>{`
        * {
          font-weight: bold;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-container {
            display: block !important;
          }
        }

        @page {
          size: 87.6mm 101.6mm;
          margin: 0;
        }

        #receipt-container {
          width: 87.6mm;
          height: 101.6mm;
          background: white;
          position: relative;
          font-family: 'Courier New', monospace;
          padding: 4mm;
          margin: 0 auto;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          border: 1px solid #e0e0e0;
        }

        .receipt-header {
          text-align: center;
          font-size: 18pt;
          font-weight: bold;
          margin-bottom: 3mm;
          padding: 0 2mm;
        }

        .receipt-divider {
          border: none;
          border-top: 2px solid #000;
          margin: 2mm 2mm;
        }

        .receipt-id {
          font-size: 12pt;
          font-weight: bold;
          margin-bottom: 2mm;
          margin-top: 2mm;
          text-align: center;
          padding: 0 2mm;
        }

        .receipt-phone {
          font-size: 10pt;
          font-weight: bold;
          margin-bottom: 2mm;
          padding: 0 2mm;
        }

        .customer-section {
          font-size: 9pt;
          font-weight: bold;
          line-height: 12pt;
          margin-bottom: 2mm;
          margin-top: 2mm;
          padding: 0 2mm;
        }

        .customer-phone {
          font-size: 11pt;
          font-weight: bold;
          margin-top: 2mm;
          margin-bottom: 2mm;
        }

        .customer-address {
          font-size: 9pt;
          font-weight: bold;
          line-height: 11pt;
        }

        .info-line {
          font-size: 10pt;
          font-weight: bold;
          line-height: 12pt;
          margin-bottom: 2mm;
          margin-top: 2mm;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 2mm;
        }

        .website-inline {
          font-size: 8pt;
          font-weight: bold;
        }

        .qr-container {
          text-align: center;
          margin: 8mm auto 3mm auto;
          padding: 0 2mm;
        }

        .qr-container canvas {
          margin: 0 auto;
          display: block;
        }

        .qr-label {
          font-size: 2px;
          font-weight: bold;
          margin-top: 1mm;
          margin-left: 2mm;
          text-align: left;
          padding: 0 2mm;
        }

        .footer {
          font-size: 8pt;
          font-weight: bold;
          position: absolute;
          bottom: 4mm;
          left: 4mm;
          right: 4mm;
          display: flex;
          justify-content: space-between;
          padding: 0 2mm;
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 p-4 no-print">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Receipt Preview</h2>
              <div className="flex gap-2">
                <button
                  onClick={printInNewWindow}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                >
                  🖨️ Print Receipt
                </button>
                <button
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              <div id="receipt-container" ref={receiptRef}>
                {/* Header - HowTech  */}
                <div className="receipt-header">
                  HowTech
                </div>

                <hr className="receipt-divider" />

                {/* Receipt ID */}
                <div className="receipt-id">
                  {order.steadfastConsignmentId || order.orderId}
                </div>

                {/* Phone */}
                <div className="receipt-phone">
                  Call: +8801711893988
                </div>

                {/* Customer Info */}
                <div className="customer-section">
                  <div>Customer: {order.customerInfo?.name || 'N/A'}</div>
                  <div className="customer-phone">
                    Phone: {order.customerInfo?.phone || 'N/A'}
                  </div>
                  <div className="customer-address">
                    Address: {order.customerInfo?.address || 'N/A'}
                  </div>
                </div>

                {/* COD and Website */}
                <div className="info-line">
                  <div>COD: {order.codForCourier || order.totalAmount || '0.00'}</div>
                  <div className="website-inline">www.howtech.com.bd</div>
                </div>

                {/* QR Code */}
                <div className="qr-container">
                  <canvas ref={qrCanvasRef}></canvas>
                </div>

                {/* Order ID under QR */}
                <div className="qr-label">
                  Order: {order.steadfastConsignmentId}-{order.orderId}
                </div>

                {/* Footer */}
                <div className="footer">
                  <span>[HowTech ]</span>
                  <span>{formatDate()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Order Summary</h3>
            <div className="space-y-4">
              {order.cartItems?.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/64';
                    }}
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      Quantity: {item.quantity} × ৳{item.price}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-800">
                      ৳{item.quantity * item.price}
                    </p>
                  </div>
                </div>
              ))}

              <div className="border-t pt-4">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total COD:</span>
                  <span>৳{order.codForCourier || order.totalAmount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Receipt;
